import { BookOpen, GraduationCap } from "lucide-react";
import { gradeOptions, teachingFocusStepCopy } from "../../data/onboarding";
import { useMasterData } from "../../context/MasterDataContext";
import { OnboardingChip } from "./OnboardingChip";
import { OnboardingFooter } from "./OnboardingFooter";
import { OnboardingOptionCard } from "./OnboardingOptionCard";
import { OnboardingSectionHeading } from "./OnboardingSectionHeading";
import { OnboardingShell } from "./OnboardingShell";

export function TeachingFocusStep({
  selectedGrades,
  selectedSubjects,
  selectedCurricula,
  onToggleGrade,
  onToggleSubject,
  onToggleCurriculum,
  onBack,
  onNext,
}) {
  const {
    subjectOptions,
    curriculumOptions,
    isLoading,
    error,
    reload,
  } = useMasterData();

  const canContinue =
    selectedGrades.length > 0 &&
    selectedSubjects.length > 0 &&
    selectedCurricula.length > 0;

  return (
    <OnboardingShell>
      <div className="max-w-4xl">
        <h1 className="text-[32px] font-bold leading-tight text-[#1B1B2F] sm:text-[36px]">
          {teachingFocusStepCopy.title}
        </h1>
        <p className="mt-3 text-[15px] text-[#6B7280]">
          {teachingFocusStepCopy.subtitle}
        </p>

        <div className="mt-8 space-y-8">
          <section>
            <OnboardingSectionHeading
              icon={GraduationCap}
              label={teachingFocusStepCopy.gradesHeading}
            />
            <div className="flex flex-wrap gap-3">
              {gradeOptions.map((grade) => (
                <OnboardingChip
                  key={grade.id}
                  label={grade.label}
                  selected={selectedGrades.includes(grade.id)}
                  onClick={() => onToggleGrade(grade.id)}
                />
              ))}
            </div>
          </section>

          <section>
            <OnboardingSectionHeading
              icon={BookOpen}
              label={teachingFocusStepCopy.subjectsHeading}
            />
            {isLoading ? (
              <p className="text-sm text-[#6B7280]">Loading subjects…</p>
            ) : error ? (
              <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-4 py-4 text-sm text-[#b91c1c]">
                <p>{error}</p>
                <button
                  type="button"
                  onClick={reload}
                  className="mt-3 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-hover"
                >
                  Retry
                </button>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {subjectOptions.map((subject) => (
                  <OnboardingOptionCard
                    key={subject.id}
                    title={subject.label}
                    selected={selectedSubjects.includes(subject.id)}
                    onClick={() => onToggleSubject(subject.id)}
                    compact
                  />
                ))}
              </div>
            )}
          </section>

          <section>
            <OnboardingSectionHeading
              icon={BookOpen}
              label={teachingFocusStepCopy.curriculumHeading}
            />
            {isLoading ? (
              <p className="text-sm text-[#6B7280]">Loading curriculum options…</p>
            ) : error ? null : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {curriculumOptions.map((curriculum) => (
                  <OnboardingOptionCard
                    key={curriculum.id}
                    title={curriculum.label}
                    subtitle={curriculum.subtitle ?? undefined}
                    selected={selectedCurricula.includes(curriculum.id)}
                    onClick={() => onToggleCurriculum(curriculum.id)}
                    compact
                  />
                ))}
              </div>
            )}
          </section>
        </div>

        <OnboardingFooter
          onBack={onBack}
          onNext={onNext}
          backLabel={teachingFocusStepCopy.backLabel}
          nextLabel={teachingFocusStepCopy.nextLabel}
          nextDisabled={!canContinue || isLoading || Boolean(error)}
        />
      </div>
    </OnboardingShell>
  );
}
